import React, { useEffect, useState } from 'react';
import { Form, Input } from 'antd';
import { useAuth } from '../context/AuthContext';
import http, { setTokens } from '../api/http';
import AuthLayout from '../components/AuthLayout';
import { MailIcon, LockIcon, GoogleG, InfoIcon } from '../components/AuthIcons';

export default function Login() {
  const { login, loading } = useAuth();
  const [form] = Form.useForm();
  const [feedback, setFeedback] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);

  async function handleCredentialResponse(response) {
    const id_token = response?.credential;
    if (!id_token) {
      setFeedback({ type: 'error', message: 'Google: id_token manquant' });
      return;
    }

    setGoogleLoading(true);
    setFeedback(null);

    try {
      const { data } = await http.post('/auth/google', { id_token });
      setTokens({ access: data.access, refresh: data.refresh });
      localStorage.setItem('user', JSON.stringify(data.user));

      if (data?.user?.status === 'approved') {
        const roleToPath = { admin: '/admin', supervisor: '/supervisor', student: '/student' };
        window.location.replace(roleToPath[data.user.role] || '/');
        return;
      }

      if (data?.user?.status === 'pending') {
        setFeedback({ type: 'info', message: 'Connexion Google réussie. Votre compte est en attente de validation (admin).' });
        return;
      }

      if (data?.user?.status === 'rejected') {
        setFeedback({ type: 'error', message: 'Connexion Google refusée : votre compte a été rejeté.' });
        return;
      }

      setFeedback({ type: 'warning', message: 'Connexion Google réussie, mais statut utilisateur inconnu.' });
    } catch (e) {
      if (e?.response?.data?.code === 'NOT_WHITELISTED') {
        setFeedback({ type: 'error', message: "Votre email n'est pas autorisé à accéder à cette application." });
      } else {
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.details ||
          e?.response?.data?.error ||
          'Connexion Google échouée';
        setFeedback({ type: 'error', message: msg });
      }
    } finally {
      setGoogleLoading(false);
    }
  }

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      setFeedback({
        type: 'error',
        message: 'Google: variable d’environnement REACT_APP_GOOGLE_CLIENT_ID manquante (client/.env).',
      });
      return;
    }

    let cancelled = false;
    const tryInit = () => {
      if (cancelled) return;
      const ok = setupGoogleAccountsId({ clientId, onCredential: handleCredentialResponse });
      if (ok) return;
      setTimeout(tryInit, 250);
    };

    tryInit();
    return () => {
      cancelled = true;
    };
  }, []);

  const onFinish = async (values) => {
    setFeedback(null);
    const res = await login(values.email, values.password);
    if (res?.ok) {
      setFeedback({ type: 'success', message: 'Connexion réussie' });
      // redirect by role
      const u = JSON.parse(localStorage.getItem('user'));
      if (u?.must_change_password) {
        setTimeout(() => {
          window.location.replace('/force-change-password');
        }, 600);
        return;
      }
      const roleToPath = { admin: '/admin', supervisor: '/supervisor', student: '/student' };
      setTimeout(() => {
        window.location.replace(roleToPath[u.role] || '/');
      }, 600);
    } else {
      setFeedback({ type: 'error', message: res?.error || 'Identifiants invalides' });
    }
  };

  return (
    <AuthLayout isSingleColumn>
      <div>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: 0, textAlign: 'center', marginBottom: 24, fontSize: 20, fontWeight: 700, color: 'var(--auth-primary-dark)' }}>IN/OUT Résidence</h2>
        </div>

        {feedback && (
          <div className={`alert-bar${feedback.type === 'error' ? ' alert-error' : feedback.type === 'success' ? ' alert-success' : ' alert-info'}`}>
            {feedback.message}
          </div>
        )}
        {googleLoading && (
          <div className="alert-bar alert-info">Connexion Google en cours...</div>
        )}

        <div className="card">
          <div className="info-bar">
            {InfoIcon}
            <div>
              <div className="info-bar-title">Connexion</div>
              <div className="info-bar-desc">Connectez-vous à votre espace.</div>
            </div>
          </div>
          
          <div className="card-inner">
            <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>
              <div className="field">
                <label>Email <span>*</span></label>
                <div className="input-wrap">
                  {MailIcon}
                  <Form.Item name="email" noStyle rules={[{ required: true, type: 'email', message: 'Email invalide' }]}>
                    <Input placeholder="email@iugb.edu.ci" bordered={false} />
                  </Form.Item>
                </div>
              </div>

              <div className="field">
                <label>Mot de passe <span>*</span></label>
                <div className="input-wrap">
                  {LockIcon}
                  <Form.Item name="password" noStyle rules={[{ required: true, message: 'Mot de passe requis' }]}>
                    <Input.Password placeholder="••••••••" bordered={false} />
                  </Form.Item>
                </div>
              </div>

              <button className="btn-primary" type="submit" disabled={loading}>
                {loading ? 'Connexion...' : 'Se connecter'}
              </button>
            </Form>

            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <div className="section-label" style={{ marginBottom: 12 }}>Ou continuer avec Google</div>
              <div id="googleBtn" style={{ display: 'flex', justifyContent: 'center' }} />
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}

// Google Identity Services setup
function setupGoogleAccountsId({ clientId, onCredential }) {
  if (typeof window === 'undefined') return false;
  const g = window.google?.accounts?.id;
  if (!g) return false;

  g.initialize({ client_id: clientId, callback: onCredential });
  const el = document.getElementById('googleBtn');
  if (el) {
    el.innerHTML = '';
    g.renderButton(el, { theme: 'outline', size: 'large', type: 'standard', width: '100%' });
  }
  // Affiche le picker si possible (sinon l'utilisateur verra le bouton classique).
  try {
    g.prompt();
  } catch {
    // ignore
  }
  return true;
}
